import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { request } from 'js/api/axios';
import { buildSteps } from './steps';

const OnboardingContext = createContext(null);

/**
 * Loads the getting-started checklist for the active workspace and shares it
 * with the dashboard card, the sidebar pill and the page hints, so they always
 * agree. Progress is refetched on every page change, which is when a step is
 * likely to have been completed, but only while the checklist is showing.
 *
 * `initiallyVisible` comes from the signed-in user, so people who dismissed the
 * checklist (or never had it) don't fetch progress they won't see.
 */
export function OnboardingProvider({
  workspaceId,
  initiallyVisible = true,
  children,
}) {
  const token = useSelector((state) => state.identity.token);
  const { pathname } = useLocation();
  const [progress, setProgress] = useState(null);
  const latestRequest = useRef(0);

  const basePath = `/api/workspaces/${workspaceId}/onboarding`;

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  // Every endpoint answers with the refreshed checklist. Only the newest
  // answer is kept, so a slow response can't overwrite a newer one.
  const send = useCallback(
    async (method, path = '', data) => {
      const requestId = ++latestRequest.current;

      try {
        const response = await request({
          method,
          url: `${basePath}${path}`,
          data,
          headers,
        });

        if (requestId === latestRequest.current) {
          setProgress(response.data.data);
        }

        return response.data.data;
      } catch (error) {
        // The checklist is a guide, never a blocker; a failed call leaves the
        // last known state in place.
        return null;
      }
    },
    [basePath, headers]
  );

  // Kept apart from the progress so they don't change identity on every
  // update, and callers can list them as effect dependencies.
  const actions = useMemo(
    () => ({
      refresh: () => send('get'),
      dismiss: () => send('post', '/dismiss'),
      restore: () => send('post', '/restore'),
      collapse: () => send('post', '/collapse'),
      expand: () => send('post', '/expand'),
      skip: (step) => send('post', `/steps/${step}/skip`),
      unskip: (step) => send('delete', `/steps/${step}/skip`),
      resultsViewed: (assessmentId) =>
        send('post', '/results-viewed', { assessment_id: assessmentId }),
      createSampleTeam: () => send('post', '/sample-team'),
    }),
    [send]
  );

  useEffect(() => {
    setProgress(null);
  }, [workspaceId]);

  // The latest answer wins over the signed-in user's flag, which goes stale as
  // soon as the checklist is dismissed or restored.
  const visible = progress ? progress.visible : initiallyVisible;
  const { refresh } = actions;

  useEffect(() => {
    if (workspaceId && token && visible) {
      refresh();
    }
  }, [workspaceId, token, visible, pathname, refresh]);

  const value = useMemo(
    () => ({
      workspaceId,
      visible,
      progress: visible ? progress : null,
      steps: visible ? buildSteps(progress, workspaceId) : [],
      ...actions,
    }),
    [workspaceId, visible, progress, actions]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

/**
 * The checklist for the active workspace, or null outside a workspace (account
 * pages, guest pages), so callers can render nothing.
 */
export function useOnboarding() {
  return useContext(OnboardingContext);
}
